/**
 * Notifications — dedicated full-history screen with inline tone control.
 * Calm thread; no badges; no urgent counters; mark items "seen" softly.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import Brand from "../components/Brand";
import { ArrowLeft, BellOff, Filter } from "lucide-react";

const TONE_PILLS = [
  { id: "minimal", label: "Minimal" },
  { id: "calm", label: "Calm" },
  { id: "detailed", label: "Detailed" },
  { id: "emergency_only", label: "Emergency-only" },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "moments ago";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(iso).toLocaleDateString();
}

const KIND_LABEL = {
  arrival: "Arrival",
  departure: "Departure",
  state_changed: "A gentle update",
  self_check_in: "Kiosk tap",
  moment_posted: "A moment",
  circle_of_calm: "Circle of calm",
  door_sensor: "Settled in",
  absence_reported: "Absence",
  absence_acknowledged: "Acknowledged",
  announcement: "From the center",
  pickup_authorized: "Trusted pickup",
};

export default function Notifications() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [tone, setTone] = useState(user?.notification_tone || "calm");
  const [busyTone, setBusyTone] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users/me/notifications", { params: { limit: 100 } });
      setItems(r.data || []);
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setTone(user?.notification_tone || "calm");
  }, [user?.notification_tone]);

  // Mark visible unseen items as seen, softly.
  useEffect(() => {
    const unseen = items.filter((n) => !n.seen_at).map((n) => n.id);
    if (unseen.length === 0) return;
    const id = setTimeout(() => {
      api
        .post("/users/me/notifications/seen", { notification_ids: unseen })
        .catch(() => {});
    }, 1200);
    return () => clearTimeout(id);
  }, [items]);

  const pickTone = async (id) => {
    if (id === tone || busyTone) return;
    setBusyTone(true);
    setTone(id);
    try {
      const r = await api.patch("/users/me", { notification_tone: id });
      setUser?.(r.data);
      toast("Tone gently updated.");
      await load();
    } catch {
      toast("Couldn't save. We'll try again, quietly.");
    } finally {
      setBusyTone(false);
    }
  };

  const filterPills = useMemo(() => {
    const kinds = new Set(items.map((n) => n.kind));
    return [{ id: "all", label: "All" }].concat(
      [...kinds].map((k) => ({ id: k, label: KIND_LABEL[k] || k }))
    );
  }, [items]);

  const visible = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((n) => n.kind === filter);
  }, [items, filter]);

  return (
    <div data-testid="notifications-screen" className="min-h-screen">
      <header className="px-5 sm:px-10 pt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="notifications-back"
          aria-label="Back"
          className="flex items-center gap-2 text-[#6A6863] hover:text-[#2C2B29] transition-colors"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="text-sm">Back</span>
        </button>
        <Brand size="sm" dataTestId="brand-notifications" />
      </header>

      <main className="px-6 sm:px-10 pt-8 pb-32 max-w-2xl mx-auto fade-up">
        <span className="eyebrow">Notifications</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight leading-none">
          A soft thread,
          <br />
          <em className="italic text-[#8A9984]">held quietly.</em>
        </h1>
        <p className="mt-4 text-base text-[#6A6863] font-light max-w-md">
          Every gentle update Alouvya has whispered to you. Choose a tone below
          to soften or detail the thread; the same history just reads differently.
        </p>

        {/* Tone pills */}
        <div
          data-testid="notifications-tone-pills"
          className="mt-8 flex flex-wrap gap-2"
        >
          <span className="trust-pill">
            <BellOff size={11} strokeWidth={1.5} />
            tone
          </span>
          {TONE_PILLS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => pickTone(p.id)}
              data-testid={`notifications-tone-${p.id}`}
              aria-pressed={tone === p.id}
              className={
                "px-4 py-2 rounded-full text-xs transition-colors " +
                (tone === p.id
                  ? "bg-[#132619] text-white"
                  : "bg-[#F2EFEA] text-[#2C2B29] hover:bg-[#E6BFA6]/40")
              }
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Filter pills */}
        {filterPills.length > 1 && (
          <div
            data-testid="notifications-filter-pills"
            className="mt-6 flex flex-wrap gap-2"
          >
            <span className="trust-pill">
              <Filter size={11} strokeWidth={1.5} />
              filter
            </span>
            {filterPills.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setFilter(p.id)}
                data-testid={`notifications-filter-${p.id}`}
                aria-pressed={filter === p.id}
                className={
                  "px-4 py-1.5 rounded-full text-[11px] transition-colors " +
                  (filter === p.id
                    ? "bg-[#2C2B29] text-white"
                    : "bg-white border hairline text-[#6A6863] hover:text-[#2C2B29]")
                }
              >
                {p.label}
              </button>
            ))}
          </div>
        )}

        <ol
          data-testid="notifications-list"
          className="mt-8 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
        >
          {loading && (
            <li className="bg-[#FBF9F6] p-6 font-serif italic text-[#6A6863]">
              A quiet moment…
            </li>
          )}
          {!loading && visible.length === 0 && (
            <li
              data-testid="notifications-empty"
              className="bg-[#FBF9F6] p-7 font-serif italic text-[#6A6863]"
            >
              {filter === "all"
                ? "All is well. Nothing new."
                : "Nothing in this filter, gently."}
            </li>
          )}
          {visible.map((n) => (
            <li
              key={n.id}
              data-testid={`notifications-item-${n.id}`}
              className={
                "p-5 sm:p-6 " +
                (n.seen_at ? "bg-[#FBF9F6]" : "bg-white")
              }
            >
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="eyebrow">
                  {KIND_LABEL[n.kind] || n.kind}
                </span>
                {n.child_name && (
                  <span className="text-[10px] tracking-[0.18em] uppercase text-[#8A9984]">
                    · {n.child_name}
                  </span>
                )}
                <span className="ml-auto text-xs text-[#6A6863]">
                  {timeAgo(n.created_at)}
                </span>
              </div>
              <p className="font-serif text-lg sm:text-xl text-[#2C2B29] leading-snug">
                {n.text}
              </p>
            </li>
          ))}
        </ol>

        <p className="mt-10 text-center text-xs text-[#6A6863] font-light italic">
          Notifications regulate calm — they never amplify anxiety.
        </p>
      </main>
    </div>
  );
}
